import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/cn';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: ReactNode;
  /** Trigger element. Muss ein einzelnes React-Element sein (kein Fragment, kein Array). */
  children: ReactElement;
  placement?: Placement;
  /** ms bis Tooltip erscheint (default 200). Ausblenden ist immer sofort. */
  delay?: number;
  /** maximale Breite in px (default 280). */
  maxWidth?: number;
  /** Wenn `true`, wird der Tooltip nicht gerendert (z. B. bei leerem Inventory-Eintrag). */
  disabled?: boolean;
}

interface Coords {
  top: number;
  left: number;
}

const ARROW_SIZE = 6; // px
const VIEWPORT_PADDING = 8; // px

/**
 * Tooltip-Komponente (Issue #73).
 *
 * - Hover & Focus öffnen nach `delay` ms; Mouseleave/Blur/Esc schließen sofort.
 * - Wird via React Portal in `document.body` gerendert (entkommt overflow:hidden).
 * - z-Index 100 — höher als Modale (z-50).
 * - Positionierung mit getBoundingClientRect(); klemmt am Viewport (Padding 8 px).
 */
export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 200,
  maxWidth = 280,
  disabled,
}: TooltipProps) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 });

  const clearShowTimer = () => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };

  const show = useCallback(() => {
    clearShowTimer();
    if (disabled) return;
    showTimerRef.current = window.setTimeout(
      () => {
        setOpen(true);
      },
      Math.max(0, delay),
    );
  }, [delay, disabled]);

  const hide = useCallback(() => {
    clearShowTimer();
    setOpen(false);
  }, []);

  // Position berechnen, sobald geöffnet (oder beim Scrollen/Resize).
  const position = useCallback(() => {
    const triggerEl = triggerRef.current;
    const tooltipEl = tooltipRef.current;
    if (!triggerEl || !tooltipEl) return;

    const trigger = triggerEl.getBoundingClientRect();
    const tip = tooltipEl.getBoundingClientRect();
    const gap = ARROW_SIZE + 2;

    let top = 0;
    let left = 0;
    switch (placement) {
      case 'bottom':
        top = trigger.bottom + gap;
        left = trigger.left + trigger.width / 2 - tip.width / 2;
        break;
      case 'left':
        top = trigger.top + trigger.height / 2 - tip.height / 2;
        left = trigger.left - tip.width - gap;
        break;
      case 'right':
        top = trigger.top + trigger.height / 2 - tip.height / 2;
        left = trigger.right + gap;
        break;
      case 'top':
      default:
        top = trigger.top - tip.height - gap;
        left = trigger.left + trigger.width / 2 - tip.width / 2;
        break;
    }

    // Viewport-Clamp (horizontal + vertikal). Position ist viewport-relativ
    // (fixed), keine Scroll-Offsets nötig.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - tip.width - VIEWPORT_PADDING));
    top = Math.max(VIEWPORT_PADDING, Math.min(top, vh - tip.height - VIEWPORT_PADDING));

    setCoords({ top, left });
  }, [placement]);

  useLayoutEffect(() => {
    if (!open) return;
    position();
    const handle = () => position();
    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);
    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
  }, [open, position]);

  // Esc schließt, wenn Trigger fokussiert ist oder Tooltip offen ist.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') hide();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, hide]);

  useEffect(() => () => clearShowTimer(), []);

  if (!isValidElement(children)) {
    if (import.meta.env.DEV) {
      console.warn('[Tooltip] children must be a single React element.');
    }
    return <>{children}</>;
  }

  // Trigger-Element clonen: Refs + Event-Handler injizieren, ohne bestehende
  // Handler des Konsumenten zu verdrängen.
  const childProps = (children as ReactElement<Record<string, unknown>>).props ?? {};
  const existingDescribedBy =
    typeof childProps['aria-describedby'] === 'string'
      ? (childProps['aria-describedby'] as string)
      : undefined;

  const triggerProps: Record<string, unknown> = {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // Forward to the original ref (string refs werden ignoriert).
      const originalRef = (children as unknown as { ref?: unknown }).ref;
      if (typeof originalRef === 'function') {
        (originalRef as (n: HTMLElement | null) => void)(node);
      } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
        (originalRef as { current: HTMLElement | null }).current = node;
      }
    },
    onMouseEnter: (e: React.MouseEvent) => {
      (childProps.onMouseEnter as ((ev: React.MouseEvent) => void) | undefined)?.(e);
      show();
    },
    onMouseLeave: (e: React.MouseEvent) => {
      (childProps.onMouseLeave as ((ev: React.MouseEvent) => void) | undefined)?.(e);
      hide();
    },
    onFocus: (e: React.FocusEvent) => {
      (childProps.onFocus as ((ev: React.FocusEvent) => void) | undefined)?.(e);
      show();
    },
    onBlur: (e: React.FocusEvent) => {
      (childProps.onBlur as ((ev: React.FocusEvent) => void) | undefined)?.(e);
      hide();
    },
    'aria-describedby': open
      ? [existingDescribedBy, tooltipId].filter(Boolean).join(' ')
      : existingDescribedBy,
  };

  const trigger = cloneElement(children, triggerProps);

  // Animation: subtiler Slide-In aus Placement-Richtung.
  const enterOffset = 4;
  const initial =
    placement === 'top'
      ? { opacity: 0, y: enterOffset }
      : placement === 'bottom'
        ? { opacity: 0, y: -enterOffset }
        : placement === 'left'
          ? { opacity: 0, x: enterOffset }
          : { opacity: 0, x: -enterOffset };

  return (
    <>
      {trigger}
      {typeof document !== 'undefined'
        ? createPortal(
            <AnimatePresence>
              {open ? (
                <motion.div
                  ref={tooltipRef}
                  id={tooltipId}
                  role="tooltip"
                  initial={initial}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={initial}
                  transition={{ duration: 0.12, ease: [0.32, 0.72, 0, 1] }}
                  style={{
                    position: 'fixed',
                    top: coords.top,
                    left: coords.left,
                    maxWidth,
                    zIndex: 100,
                    pointerEvents: 'none',
                  }}
                  className={cn(
                    'rounded bg-ink-900 px-3 py-2 text-xs leading-snug text-paper shadow-elev-3',
                  )}
                >
                  {content}
                  <TooltipArrow placement={placement} />
                </motion.div>
              ) : null}
            </AnimatePresence>,
            document.body,
          )
        : null}
    </>
  );
}

function TooltipArrow({ placement }: { placement: Placement }) {
  const base = 'absolute h-2 w-2 rotate-45 bg-ink-900';
  const pos =
    placement === 'top'
      ? '-bottom-1 left-1/2 -translate-x-1/2'
      : placement === 'bottom'
        ? '-top-1 left-1/2 -translate-x-1/2'
        : placement === 'left'
          ? '-right-1 top-1/2 -translate-y-1/2'
          : '-left-1 top-1/2 -translate-y-1/2';
  return <span aria-hidden="true" className={cn(base, pos)} />;
}
